import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Gift, X, ChevronDown, ChevronUp } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";
import { formatDistanceToNow } from "date-fns";

interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  read: boolean;
  action_url: string | null;
  created_at: string;
}

const typeColors: Record<string, string> = {
  milestone: "text-yellow-500",
  challenge: "text-primary",
  giveaway: "text-green-500",
  announcement: "text-blue-400",
  promo: "text-purple-400",
  store: "text-orange-400",
  custom: "text-muted-foreground",
};

const typeLabels: Record<string, string> = {
  milestone: "🏆 Milestone",
  challenge: "🎮 Challenge",
  giveaway: "🎁 Giveaway",
  announcement: "📢 News",
  promo: "🎫 Promo",
  store: "🛒 Store",
  custom: "📬 Message",
};

const MESSAGE_CHAR_LIMIT = 100;

function NotificationMessage({ message }: { message: string }) {
  const [expanded, setExpanded] = useState(false);
  const isLong = message.length > MESSAGE_CHAR_LIMIT;

  if (!isLong) {
    return <p className="text-xs text-muted-foreground mt-0.5">{message}</p>;
  }

  return (
    <div className="mt-0.5">
      <p className="text-xs text-muted-foreground">
        {expanded ? message : `${message.slice(0, MESSAGE_CHAR_LIMIT)}…`}
      </p>
      <button
        onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
        className="text-[10px] text-primary hover:text-primary/80 font-medium mt-0.5 flex items-center gap-0.5"
      >
        {expanded ? (<>Show less <ChevronUp className="h-2.5 w-2.5" /></>) : (<>Read more <ChevronDown className="h-2.5 w-2.5" /></>)}
      </button>
    </div>
  );
}

export function NotificationBell() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [open, setOpen] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const [hasDailyBonus, setHasDailyBonus] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUserId(session?.user?.id ?? null);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      setUserId(session?.user?.id ?? null);
    });
    return () => subscription.unsubscribe();
  }, []);

  // Check if daily login bonus is unclaimed
  useEffect(() => {
    const checkDailyBonus = async () => {
      if (!userId) { setHasDailyBonus(false); return; }

      const { data: profile } = await supabase
        .from("profiles")
        .select("onboarding_completed")
        .eq("id", userId)
        .maybeSingle();

      if (!profile?.onboarding_completed) { setHasDailyBonus(false); return; }

      const today = new Date().toISOString().split("T")[0];
      const { data: loginRecord } = await supabase
        .from("daily_logins")
        .select("last_login_date")
        .eq("user_id", userId)
        .maybeSingle();

      setHasDailyBonus(!loginRecord || loginRecord.last_login_date !== today);
    };

    checkDailyBonus();

    const handleClaimed = () => setHasDailyBonus(false);
    window.addEventListener("daily-login-claimed", handleClaimed);
    return () => window.removeEventListener("daily-login-claimed", handleClaimed);
  }, [userId]);

  const fetchNotifications = useCallback(async () => {
    if (!userId) return;
    const { data } = await supabase
      .from("notifications")
      .select("*")
      .eq("user_id", userId)
      .order("created_at", { ascending: false })
      .limit(50);
    setNotifications((data as Notification[]) || []);
  }, [userId]);

  useEffect(() => {
    if (!userId) {
      setNotifications([]);
      return;
    }
    fetchNotifications();

    const channel = supabase
      .channel(`notifications:${userId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "notifications",
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          setNotifications((prev) => [payload.new as Notification, ...prev]);
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [userId, fetchNotifications]);

  const unreadCount = notifications.filter((n) => !n.read).length + (hasDailyBonus ? 1 : 0);

  const markAllRead = async () => {
    if (!userId) return;
    if (notifications.filter((n) => !n.read).length > 0) {
      await supabase
        .from("notifications")
        .update({ read: true })
        .eq("user_id", userId)
        .eq("read", false);
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    }
  };

  const deleteNotification = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!userId) return;
    // Optimistic update
    setNotifications((prev) => prev.filter((n) => n.id !== id));
    await supabase
      .from("notifications")
      .delete()
      .eq("id", id)
      .eq("user_id", userId);
  };

  const clearReadNotifications = async () => {
    if (!userId) return;
    // Optimistic update
    setNotifications((prev) => prev.filter((n) => !n.read));
    await supabase
      .from("notifications")
      .delete()
      .eq("user_id", userId)
      .eq("read", true);
  };

  const handleOpen = (isOpen: boolean) => {
    setOpen(isOpen);
    if (isOpen) markAllRead();
  };

  const handleNotificationClick = (notif: Notification) => {
    setOpen(false);
    if (notif.action_url) navigate(notif.action_url);
  };

  const hasReadNotifications = notifications.some((n) => n.read);

  if (!userId) return null;

  return (
    <Popover open={open} onOpenChange={handleOpen}>
      <PopoverTrigger asChild>
        <button
          className="relative flex items-center justify-center h-8 w-8 rounded-lg hover:bg-muted/50 transition-colors"
          aria-label="Notifications"
        >
          <Bell className="h-4 w-4 text-muted-foreground hover:text-foreground transition-colors" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-primary text-[10px] flex items-center justify-center font-bold text-primary-foreground shadow-[0_0_8px_hsl(var(--primary))] animate-pulse">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-80 p-0 border border-border/50 bg-card shadow-lg"
        align="end"
        sideOffset={8}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-border/50">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-sm text-foreground">Notifications</h3>
            {unreadCount > 0 && (
              <span className="text-xs text-primary font-medium">{unreadCount} new</span>
            )}
          </div>
          {hasReadNotifications && (
            <button
              onClick={clearReadNotifications}
              className="text-xs text-muted-foreground hover:text-destructive transition-colors"
            >
              Clear read
            </button>
          )}
        </div>

        {!hasDailyBonus && notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-muted-foreground">
            <Bell className="h-8 w-8 mb-2 opacity-30" />
            <p className="text-sm">No notifications yet</p>
          </div>
        ) : (
          <ScrollArea className="max-h-[400px]">
            <div className="divide-y divide-border/30">
              {/* Daily login bonus virtual notification */}
              {hasDailyBonus && (
                <button
                  onClick={() => { setOpen(false); navigate("/daily-login"); }}
                  className="w-full text-left px-4 py-3 hover:bg-muted/30 transition-colors bg-primary/5 cursor-pointer"
                >
                  <div className="flex items-start gap-2">
                    <span className="mt-1.5 h-2 w-2 rounded-full bg-primary shrink-0 shadow-[0_0_6px_hsl(var(--primary))]" />
                    <div className="flex-1">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <Gift className="h-3 w-3 text-primary" />
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-primary">
                          Daily Bonus
                        </span>
                      </div>
                      <p className="text-sm font-medium text-foreground leading-tight">Daily Login Bonus Ready! 🎁</p>
                      <p className="text-xs text-muted-foreground mt-0.5">Your daily points are waiting — don't break your streak!</p>
                    </div>
                  </div>
                </button>
              )}
              {notifications.map((notif) => (
                <div
                  key={notif.id}
                  className={`relative group px-4 py-3 transition-colors ${
                    !notif.read ? "bg-primary/5" : ""
                  } ${notif.action_url ? "cursor-pointer hover:bg-muted/30" : "hover:bg-muted/20"}`}
                  onClick={() => handleNotificationClick(notif)}
                >
                  <div className="flex items-start gap-2 pr-5">
                    {!notif.read && (
                      <span className="mt-1.5 h-2 w-2 rounded-full bg-primary shrink-0 shadow-[0_0_6px_hsl(var(--primary))]" />
                    )}
                    <div className={`flex-1 ${notif.read ? "ml-4" : ""}`}>
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <span className={`text-[10px] font-semibold uppercase tracking-wide ${typeColors[notif.type] || typeColors.custom}`}>
                          {typeLabels[notif.type] || notif.type}
                        </span>
                      </div>
                      <p className="text-sm font-medium text-foreground leading-tight">{notif.title}</p>
                      <NotificationMessage message={notif.message} />
                      <p className="text-[10px] text-muted-foreground/60 mt-1">
                        {formatDistanceToNow(new Date(notif.created_at), { addSuffix: true })}
                      </p>
                    </div>
                  </div>
                  {/* Dismiss button */}
                  <button
                    onClick={(e) => deleteNotification(notif.id, e)}
                    className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-muted/60 text-muted-foreground hover:text-foreground"
                    aria-label="Dismiss notification"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
      </PopoverContent>
    </Popover>
  );
}

export default NotificationBell;
